/**
 * UC3 launch updates (client, 8 Oct 2026, items 1 and 5): the isolated geography on the
 * map.
 *
 * While `intel.isolate` is set (the UC3 Geography step), `HierarchyLayers` draws nothing
 * and this component draws the one unit in its place: its outline (`path.intel-isolate`,
 * still clickable, so a click selects it like any boundary) and its name. It also masks the
 * raster pane to that unit's polygon with the same layer-point `clip-path` the AOI clip and
 * the swipe write (`ringsClipPath`), so the crop, productivity, vegetation, rainfall and
 * soil rasters stop at the selection's edge. No server clip and no request of its own: both
 * payloads are the `/hierarchy/units` cache entries `GeographyFocus` and `useUnitLink`
 * already hold.
 *
 * `data-isolate-count` on the map container says how many unit outlines are drawn (0 or
 * 1), for the end-to-end checks.
 */
import { useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import L from 'leaflet';

import { fetchUnits, queryKeys } from '../../lib/api';
import { outerRingsOf } from '../../lib/geo';
import { useAppStore } from '../../lib/state';
import { isolateFeature } from './isolate';
import { useMap } from './mapContext';
import { ringsClipPath, type LonLatRing } from './ProgrammeClip';
import { SCOPE_CLIP_PANE, setScopeClip } from './ScopeClip';
import { createUnitLabels } from './unitLabels';

type GeoJsonInput = Parameters<typeof L.geoJSON>[0];

export const ISOLATE_CLASS = 'intel-isolate';
export const ISOLATE_CAP_CLASS = 'intel-isolate-cap';
const ISOLATE_STYLE: L.PathOptions = {
  color: '#1D4F7C',
  weight: 2.6,
  opacity: 1,
  fillColor: '#2B6CB0',
  fillOpacity: 0,
  className: ISOLATE_CLASS,
};

export function IntelIsolate() {
  const map = useMap();
  const isolate = useAppStore((s) => s.intel.isolate);
  const aoiClip = useAppStore((s) => s.aoiClip);
  const setSelectedUnit = useAppStore((s) => s.setSelectedUnit);
  const setLastClickLatLng = useAppStore((s) => s.setLastClickLatLng);

  const { data: admin0 } = useQuery({
    queryKey: queryKeys.units('admin', 0, 'ALL', 'z7'),
    queryFn: () => fetchUnits('admin', 0, 'ALL', 'z7'),
    enabled: isolate?.level === 'admin0',
    staleTime: 30 * 60 * 1000,
  });
  const { data: watersheds } = useQuery({
    queryKey: queryKeys.units('watershed', null, 'ALL', 'z7', null),
    queryFn: () => fetchUnits('watershed', null, 'ALL', 'z7', null),
    enabled: isolate?.level === 'watershed',
    staleTime: 30 * 60 * 1000,
  });

  const feature = useMemo(() => isolateFeature(isolate, admin0, watersheds), [isolate, admin0, watersheds]);
  // NOTES #753: a stroke-only, non-interactive copy of the outline (`path.intel-isolate-cap`)
  // is kept on top of the unit layers the flow adds later in the same `resultPane` (the
  // productivity terciles, the Analyse choropleth), so the edge reads above their fills
  // while clicks still reach the units under it.
  const outline = useRef<L.GeoJSON | null>(null);
  useEffect(() => {
    const raise = (e: L.LayerEvent) => {
      const current = outline.current;
      if (current && e.layer !== current && !current.hasLayer(e.layer)) current.bringToFront();
    };
    map.on('layeradd', raise);
    return () => {
      map.off('layeradd', raise);
    };
  }, [map]);

  // The outline and the label.
  useEffect(() => {
    const container = map.getContainer();
    if (!feature) {
      container.dataset.isolateCount = '0';
      return;
    }
    const layer = L.geoJSON({ type: 'FeatureCollection', features: [feature] } as unknown as GeoJsonInput, {
      pane: 'resultPane',
      interactive: true,
      style: () => ISOLATE_STYLE,
      onEachFeature: (_f, lyr) => {
        lyr.on('click', (e: L.LeafletMouseEvent) => {
          setLastClickLatLng([e.latlng.lat, e.latlng.lng]);
          setSelectedUnit(feature.properties.id);
        });
      },
    }).addTo(map);
    const cap = L.geoJSON({ type: 'FeatureCollection', features: [feature] } as unknown as GeoJsonInput, {
      pane: 'resultPane',
      interactive: false,
      style: () => ({ ...ISOLATE_STYLE, fill: false, className: ISOLATE_CAP_CLASS }),
    }).addTo(map);
    outline.current = cap;
    const labels = createUnitLabels([feature]).addTo(map);
    container.dataset.isolateCount = '1';
    return () => {
      map.removeLayer(layer);
      map.removeLayer(cap);
      map.removeLayer(labels);
      if (outline.current === cap) outline.current = null;
      container.dataset.isolateCount = '0';
    };
  }, [map, feature, setSelectedUnit, setLastClickLatLng]);

  // The raster mask. The AOI clip, when the reader has one on, keeps the pane.
  const rings = useMemo(
    () => (feature ? (outerRingsOf(feature.geometry) as LonLatRing[]) : []),
    [feature],
  );
  useEffect(() => {
    if (aoiClip || !rings.length) return;
    const apply = () => setScopeClip(map, ringsClipPath(map, rings));
    apply();
    map.on('move zoom viewreset resize', apply);
    return () => {
      map.off('move zoom viewreset resize', apply);
      const pane = map.getPane(SCOPE_CLIP_PANE);
      if (pane) pane.style.clipPath = '';
    };
  }, [map, rings, aoiClip]);

  return null;
}

export default IntelIsolate;
